import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../common/Icon';
import { useOverlayBehavior } from './Modal';

/**
 * Sheet — bottom-anchored slide-up panel. Always anchored to the
 * bottom on every viewport (unlike `<Modal>` which centers on
 * desktop). Use this for:
 *
 *   - Inline forms or filters that don't deserve a full modal
 *   - Pickers (date, time, agent, language)
 *   - Multi-step inputs (skill teach flow, profile edit)
 *   - "Quick action" panels reachable from a tab bar button
 *
 * For a list of action buttons (`Edit / Delete / Cancel`) prefer
 * the existing `<ActionSheet>` from `@/components/common`. For a
 * centered confirmation dialog use `<Modal>` or `<ConfirmDialog>`.
 *
 * Visual treatment:
 *   - Always slides up from the bottom (no center-zoom on desktop)
 *   - Top edge has a small drag handle indicator
 *   - Rounded only on the top corners
 *   - Width caps at `max-w-md` and centers horizontally on desktop
 *
 * @example
 * <Sheet
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Pick a language"
 * >
 *   <List>
 *     {LANGS.map(l => <ListRow key={l.code} title={l.name} onClick={...} />)}
 *   </List>
 * </Sheet>
 */
export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  hideHandle?: boolean;
  /** When `false`, escape and overlay clicks no longer dismiss. */
  dismissible?: boolean;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  hideHandle,
  dismissible = true,
}: SheetProps) {
  useOverlayBehavior(open, onOpenChange, dismissible);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 animate-in fade-in-0 duration-200"
        onClick={() => { if (dismissible) onOpenChange(false); }}
      />

      {/* Frame */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        className="
          relative w-full sm:max-w-md
          max-h-[85vh]
          flex flex-col
          bg-white dark:bg-[#171719] text-gray-900 dark:text-gray-100
          rounded-t-lg shadow-2xl
          animate-in fade-in-0 slide-in-from-bottom-full duration-300
        "
      >
        {/* Drag handle — visual cue only */}
        {!hideHandle && (
          <div className="flex-shrink-0 flex justify-center pt-2 pb-1">
            <div className="w-8 h-1 rounded-full bg-gray-300 dark:bg-white/[0.15]" />
          </div>
        )}

        {/* Header */}
        {title && (
          <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                {title}
              </h2>
              {description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className="
                flex-shrink-0 -mt-1 -mr-1 w-8 h-8 rounded-full flex items-center justify-center
                text-gray-500 dark:text-gray-400
                hover:bg-gray-100 dark:hover:bg-white/[0.06]
                transition-colors
              "
            >
              <Icon name="close" size={18} />
            </button>
          </div>
        )}

        {/* Scrollable body */}
        <div className="flex-1 min-h-0 overflow-y-auto">{children}</div>

        {/* Sticky footer */}
        {footer && (
          <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-white/[0.06]">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
